import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { distinctUntilChanged, filter, first, Subscription } from 'rxjs';
import { CepApi, CepApiInfo } from 'src/app/models/CepApi.model';
import { CepService } from 'src/app/services/cep/cep.service';
import { InvalidFieldMessageService } from 'src/app/services/invalid-field-message/invalid-field-message.service';
const ALPHA_NUMERIC_REGEX = /^[a-zA-Z0-9-/]*$/; //allow: - and / 
@Component({
  selector: 'app-cep',
  templateUrl: './cep.component.html',
  styleUrls: ['./cep.component.scss']
})
export class CepComponent implements OnInit, OnDestroy {

  get messageError() {
    return this.messageService;
  }

  @Input() controlName!: string;
  @Input() label!: string;
  form!: FormGroup;
  formControlAddress!: FormGroup
  private cepSearch$!: Subscription
  private valueChanges$!: Subscription

  constructor(private rootFormGroup: FormGroupDirective,
    private messageService: InvalidFieldMessageService,
    private cepService: CepService,) { }

  ngOnDestroy(): void {
    this.valueChanges$.unsubscribe();
    this.cepSearch$?.unsubscribe();
  }

  ngOnInit(): void {
    this.form = this.rootFormGroup.control;
    this.formControlAddress = this.form.controls[this.controlName] as FormGroup;
    this.formatIfHasValue(this.formControlAddress.controls['cep'].value)
    this.setValidators();
    this.detectValueChanges();
    this.searchCep();
  }

  private formatIfHasValue(cep: string) {
    if (cep) {
      this.formControlAddress.controls['cep'].setValue(this.maskCep(cep))
      this.disableFields();
    }
  }

  searchCep() {
    this.formControlAddress.controls['cep'].valueChanges.pipe(filter(cep => cep?.length == 9)).subscribe(cep => {
      this.cepSearch$ = this.cepService.buscaCep(cep).pipe(first()).subscribe((r: CepApi) => {
        if (r.success) {
          this.setAddress(r.info);
          return;
        } else {
          this.clearAddress();
        }
      })
    });
  }

  private setValidators() {
    this.formControlAddress.controls['cep'].addValidators([
      Validators.minLength(9),
    ]);
    this.formControlAddress.controls['state'].addValidators([
      Validators.minLength(2), Validators.pattern('[a-zA-Z ]*')
    ]);
    this.formControlAddress.controls['number'].addValidators([
      Validators.maxLength(10), Validators.pattern(ALPHA_NUMERIC_REGEX)
    ]);
    this.formControlAddress.controls['complement'].addValidators(Validators.maxLength(250));
  }

  private clearAddress() {
    this.formControlAddress.patchValue({
      cep: "",
      address: "",
      district: "",
      complement: "",
      city: "",
      state: "",
      ibge: "",
    });
    this.formControlAddress.enable();
  }

  private setAddress(address: CepApiInfo) {
    this.formControlAddress.patchValue({
      address: address.address,
      district: address.district,
      complement: address.complement,
      city: address.city,
      state: address.stateUF,
      ibge: address.ibge,
    })
    this.disableFields();
  }

  private disableFields() {
    //Some Ceps searchs could bring some empty fields
    if (this.formControlAddress.controls['address'].value)
      this.formControlAddress.controls['address'].disable();
    else
      this.formControlAddress.controls['address'].enable();

    if (this.formControlAddress.controls['district'].value)
      this.formControlAddress.controls['district'].disable();
    else
      this.formControlAddress.controls['district'].enable();

    if (this.formControlAddress.controls['city'].value)
      this.formControlAddress.controls['city'].disable();
    else
      this.formControlAddress.controls['city'].enable();

    if (this.formControlAddress.controls['state'].value)
      this.formControlAddress.controls['state'].disable();
    else
      this.formControlAddress.controls['city'].enable();
  }

  private detectValueChanges() {
    this.valueChanges$ = this.formControlAddress.controls['cep'].valueChanges.pipe(distinctUntilChanged())
      .subscribe((value: string) => {
        if (value.replace(" ", "")) {
          this.formControlAddress.controls['cep'].setValue(this.maskCep(value.replace(/\D/g, '')));
        }
      });
  }

  private maskCep(cepOnlyNumbers: string): string {
    return cepOnlyNumbers.replace(/[^\d]+/g, '').replace(/^(\d{5})(\d)/, '$1-$2');
  }

  public async validateUf() {
    const uf: string = this.formControlAddress.controls['state'].value;
    if (uf.length == 2) {
      await this.cepService.ufExists(uf).then(response => {
        if (!response) {
          this.formControlAddress.controls['state'].setErrors({ ufNotExist: true });
        } else {
          this.formControlAddress.setErrors(null);
        }
      })
    }
  }
}
